import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';

const VERSION_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', 
    '#06b6d4', '#6366f1', '#14b8a6', '#f97316', '#a855f7'
];

const VersionTrackingPanel = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        versionDistribution: {},
        lastUpdated: 0
    });
    const [allUsersByVersion, setAllUsersByVersion] = useState({});
    const [usersByVersion, setUsersByVersion] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [versionSearch, setVersionSearch] = useState('');
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
    const [error, setError] = useState(null);

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fuente canónica principal: /userVersions (Firebase RTDB)
            const userVerSnap = await get(ref(database, 'userVersions')).catch(err => {
                console.warn('Lectura de userVersions con advertencia:', err);
                return null;
            });

            // Fuentes secundarias de respaldo si hiciera falta cruzar información
            const [statsSnap, distSnap, usersSnap] = await Promise.all([
                get(ref(database, 'admin/versionStats')).catch(() => null),
                get(ref(database, 'admin/versionIndex')).catch(() => null),
                get(ref(database, 'users')).catch(() => null)
            ]);

            const distribution = {};
            const userMapByVer = {};
            let totalUsersCount = 0;
            let activeCount = 0;
            const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
            const now = Date.now();

            const usersData = (usersSnap && usersSnap.exists()) ? usersSnap.val() : {};

            if (userVerSnap && userVerSnap.exists()) {
                const rawUserVersions = userVerSnap.val();
                Object.entries(rawUserVersions).forEach(([uid, u]) => {
                    if (!u || uid.startsWith('_')) return;
                    totalUsersCount++;

                    // 1. Extraer nombre de versión normalizado
                    let verName = 'Desconocida';
                    if (typeof u.currentVersion === 'string') {
                        verName = u.currentVersion;
                    } else if (u.currentVersion?.versionName) {
                        verName = u.currentVersion.versionName;
                    } else if (u.versionName) {
                        verName = u.versionName;
                    }
                    verName = String(verName).replace(/,/g, '.').trim() || 'Desconocida';

                    // 2. Extraer última actividad y conteo activo
                    const lastLogin = u.lastLogin || u.web_session?.lastAccess || u.lastAccess || usersData[uid]?.lastLogin || 0;
                    if (lastLogin && (now - lastLogin) < THIRTY_DAYS) {
                        activeCount++;
                    }

                    // 3. Determinar plataforma
                    let platform = u.platform || u.web_session?.platform;
                    if (!platform) {
                        platform = u.web_session ? 'Web Browser' : 'Android';
                    }

                    const userRecord = {
                        userId: uid,
                        userEmail: u.userEmail || usersData[uid]?.email || 'Desconocido / Anónimo',
                        lastLogin,
                        platform,
                        versionName: verName,
                        versionHistory: Array.isArray(u.versionHistory)
                            ? u.versionHistory
                            : (u.versionHistory ? Object.values(u.versionHistory) : []),
                        deviceId: u.deviceId || u.web_session?.deviceId,
                        ...u
                    };

                    if (!distribution[verName]) {
                        distribution[verName] = { count: 0, uids: [] };
                        userMapByVer[verName] = [];
                    }
                    distribution[verName].count++;
                    distribution[verName].uids.push(uid);
                    userMapByVer[verName].push(userRecord);
                });
            } else if (distSnap && distSnap.exists()) {
                // Fallback a admin/versionIndex si userVersions no estuviera disponible
                const distData = distSnap.val();
                Object.entries(distData).forEach(([key, val]) => {
                    const versionName = val.__name || key.replace(/,/g, '.');
                    const uids = Object.keys(val).filter(k => k !== '__name');
                    totalUsersCount += uids.length;
                    distribution[versionName] = { count: uids.length, uids };
                });
            }

            // Fallback de conteo total si userVersions estaba vacío pero users existe
            if (totalUsersCount === 0 && usersSnap && usersSnap.exists()) {
                Object.entries(usersData).forEach(([uid, u]) => {
                    if (uid.startsWith('_')) return;
                    totalUsersCount++;
                    if (u.lastLogin && (now - u.lastLogin) < THIRTY_DAYS) {
                        activeCount++;
                    }
                });
            }

            if (totalUsersCount === 0 && statsSnap && statsSnap.exists()) {
                totalUsersCount = statsSnap.val().totalUsers || 0;
            }

            const newStats = {
                totalUsers: totalUsersCount,
                activeUsers: activeCount,
                versionDistribution: distribution,
                lastUpdated: Date.now()
            };

            setStats(newStats);
            setAllUsersByVersion(userMapByVer);

            // Auto-seleccionar la versión más usada
            const sortedVersions = Object.entries(distribution).sort((a, b) => b[1].count - a[1].count);
            if (sortedVersions.length > 0) {
                const topVer = sortedVersions[0][0];
                fetchUsersForVersion(topVer, distribution, userMapByVer);
            } else {
                setSelectedVersion(null);
                setUsersByVersion([]);
            }
        } catch (err) {
            console.error('Error cargando estadísticas:', err);
            setError('Error al cargar las estadísticas de versión.');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsersForVersion = async (versionName, currentDist = stats.versionDistribution, userMap = allUsersByVersion) => {
        setLoadingUsers(true);
        setSelectedVersion(versionName);
        try {
            // Si ya tenemos los usuarios en memoria (desde userVersions directo), usarlos al instante sin queries adicionales
            if (userMap && userMap[versionName] && userMap[versionName].length > 0) {
                setUsersByVersion(userMap[versionName]);
                return;
            }

            // Fallback de consulta bajo demanda por UID si provino de versionIndex
            const uids = currentDist[versionName]?.uids || [];
            const userPromises = uids.map(async (uid) => {
                try {
                    const userVerRef = ref(database, `userVersions/${uid}`);
                    const snap = await get(userVerRef);
                    return snap.exists() ? snap.val() : { userId: uid, userEmail: 'Desconocido / Anónimo' };
                } catch {
                    return { userId: uid, userEmail: 'Desconocido / Anónimo' };
                }
            });

            const usersData = (await Promise.all(userPromises)).filter(u => u !== null);
            setUsersByVersion(usersData);
        } catch (err) {
            console.error('Error cargando usuarios por versión:', err);
            setError('Error al obtener el detalle de usuarios.');
        } finally {
            setLoadingUsers(false);
        }
    };

    const filteredUsers = usersByVersion.filter(u =>
        (u.userEmail || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.userId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.platform || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sortedVersionEntries = Object.entries(stats.versionDistribution)
        .sort((a, b) => b[1].count - a[1].count)
        .filter(([ver]) => ver.toLowerCase().includes(versionSearch.toLowerCase()));

    const topVersion = sortedVersionEntries.length > 0 ? sortedVersionEntries[0] : null;
    const topVersionName = topVersion ? topVersion[0] : 'N/A';
    const topVersionShare = topVersion && stats.totalUsers > 0 
        ? ((topVersion[1].count / stats.totalUsers) * 100).toFixed(0) 
        : 0;

    return (
        <div className="vt-container">
            {/* Header principal */}
            <div className="vt-header">
                <div className="vt-header-title">
                    <span className="vt-badge-icon">📈</span>
                    <div>
                        <h2>Tracking & Auditoría de Versiones</h2>
                        <p>Monitoreo en tiempo real de adopción por cliente y sesión de usuario</p>
                    </div>
                </div>
                <button className="vt-btn-refresh" onClick={loadStatistics} disabled={loading}>
                    {loading ? '⏳ Actualizando...' : '🔄 Sincronizar'}
                </button>
            </div>

            {error && (
                <div className="vt-error-banner">
                    <span>⚠️ {error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {/* KPI Summary Cards */}
            <div className="vt-kpi-grid">
                <div className="vt-kpi-card">
                    <div className="kpi-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}>👥</div>
                    <div className="kpi-body">
                        <span className="kpi-label">TOTAL USUARIOS</span>
                        <div className="kpi-value">{stats.totalUsers.toLocaleString()}</div>
                        <span className="kpi-subtext">Registrados en el sistema</span>
                    </div>
                </div>

                <div className="vt-kpi-card">
                    <div className="kpi-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399' }}>⚡</div>
                    <div className="kpi-body">
                        <span className="kpi-label">ACTIVOS (30 DÍAS)</span>
                        <div className="kpi-value">{stats.activeUsers.toLocaleString()}</div>
                        <span className="kpi-subtext">
                            {stats.totalUsers > 0 ? `${((stats.activeUsers / stats.totalUsers) * 100).toFixed(1)}%` : '0%'} de retención
                        </span>
                    </div>
                </div>

                <div className="vt-kpi-card">
                    <div className="kpi-icon" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>🏆</div>
                    <div className="kpi-body">
                        <span className="kpi-label">VERSIÓN MÁS USADA</span>
                        <div className="kpi-value" style={{ fontSize: '1.25rem' }}>v{topVersionName}</div>
                        <span className="kpi-subtext">{topVersionShare}% cuota de mercado</span>
                    </div>
                </div>

                <div className="vt-kpi-card">
                    <div className="kpi-icon" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa' }}>📌</div>
                    <div className="kpi-body">
                        <span className="kpi-label">DIVERSIDAD DE VERSIONES</span>
                        <div className="kpi-value">{Object.keys(stats.versionDistribution).length}</div>
                        <span className="kpi-subtext">Versiones detectadas en uso</span>
                    </div>
                </div>
            </div>

            {/* Barra de Distribución Visual Segmentada */}
            {stats.totalUsers > 0 && Object.keys(stats.versionDistribution).length > 0 && (
                <div className="vt-segment-container">
                    <div className="vt-segment-header">
                        <span>DISTRIBUCIÓN DE ADOPCIÓN DE COMPILACIONES</span>
                        <span className="vt-timestamp">Último cambio: {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleTimeString() : 'N/A'}</span>
                    </div>
                    <div className="vt-segment-bar">
                        {sortedVersionEntries.map(([ver, data], i) => {
                            const pct = (data.count / stats.totalUsers) * 100;
                            const color = VERSION_COLORS[i % VERSION_COLORS.length];
                            if (pct < 0.5) return null;
                            return (
                                <div
                                    key={ver}
                                    className="vt-segment-fill"
                                    style={{ width: `${pct}%`, background: color }}
                                    title={`v${ver}: ${data.count} usuarios (${pct.toFixed(1)}%)`}
                                    onClick={() => fetchUsersForVersion(ver)}
                                />
                            );
                        })}
                    </div>
                    <div className="vt-segment-legend">
                        {sortedVersionEntries.slice(0, 5).map(([ver, data], i) => {
                            const color = VERSION_COLORS[i % VERSION_COLORS.length];
                            const pct = stats.totalUsers > 0 ? ((data.count / stats.totalUsers) * 100).toFixed(1) : 0;
                            return (
                                <div key={ver} className="vt-legend-item" onClick={() => fetchUsersForVersion(ver)}>
                                    <span className="vt-legend-dot" style={{ background: color }} />
                                    <span className="vt-legend-name">v{ver}</span>
                                    <span className="vt-legend-pct">({pct}%)</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Layout Principal Dual Pane de Alta Densidad */}
            <div className="vt-main-grid">
                
                {/* Panel Izquierdo: Lista Compacta de Versiones */}
                <div className="vt-side-panel">
                    <div className="vt-side-header">
                        <h3>Versiones ({sortedVersionEntries.length})</h3>
                        <input
                            type="text"
                            placeholder="Buscar versión..."
                            value={versionSearch}
                            onChange={(e) => setVersionSearch(e.target.value)}
                            className="vt-mini-search"
                        />
                    </div>

                    <div className="vt-version-scroll">
                        {sortedVersionEntries.map(([ver, data], idx) => {
                            const isSelected = selectedVersion === ver;
                            const pct = stats.totalUsers > 0 ? ((data.count / stats.totalUsers) * 100).toFixed(1) : 0;
                            const isTop = idx === 0;
                            const color = VERSION_COLORS[idx % VERSION_COLORS.length];

                            return (
                                <div
                                    key={ver}
                                    className={`vt-version-item ${isSelected ? 'active' : ''}`}
                                    onClick={() => fetchUsersForVersion(ver)}
                                >
                                    <div className="vt-ver-item-top">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="vt-color-indicator" style={{ background: color }} />
                                            <span className="vt-ver-title">v{ver}</span>
                                            {isTop && <span className="vt-top-chip">LÍDER</span>}
                                        </div>
                                        <span className="vt-ver-count">{data.count} usu.</span>
                                    </div>

                                    <div className="vt-ver-progress-wrapper">
                                        <div className="vt-ver-progress-bg">
                                            <div
                                                className="vt-ver-progress-fill"
                                                style={{ width: `${pct}%`, background: color }}
                                            />
                                        </div>
                                        <span className="vt-ver-pct">{pct}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Panel Derecho: Detalle de Usuarios de la Versión Seleccionada */}
                <div className="vt-detail-panel">
                    {selectedVersion ? (
                        <>
                            <div className="vt-detail-header">
                                <div className="vt-detail-title">
                                    <span className="vt-version-badge">v{selectedVersion}</span>
                                    <div>
                                        <h4>Usuarios instalados / activos</h4>
                                        <span className="vt-detail-subtitle">
                                            {stats.versionDistribution[selectedVersion]?.count || 0} usuarios asignados a este build
                                        </span>
                                    </div>
                                </div>

                                <div className="vt-detail-actions">
                                    <div className="vt-view-toggle">
                                        <button
                                            className={viewMode === 'table' ? 'active' : ''}
                                            onClick={() => setViewMode('table')}
                                            title="Vista en Tabla Completa"
                                        >
                                            📋 Tabla
                                        </button>
                                        <button
                                            className={viewMode === 'cards' ? 'active' : ''}
                                            onClick={() => setViewMode('cards')}
                                            title="Vista en Tarjetas Compactas"
                                        >
                                            🎴 Tarjetas
                                        </button>
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="🔍 Buscar por email o ID..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="vt-user-search"
                                    />
                                </div>
                            </div>

                            {/* Contenido de Usuarios */}
                            <div className="vt-user-content-box">
                                {loadingUsers ? (
                                    <div className="vt-loading-state">
                                        <div className="vt-spinner" />
                                        <span>Cargando detalle de usuarios para v{selectedVersion}...</span>
                                    </div>
                                ) : filteredUsers.length > 0 ? (
                                    viewMode === 'table' ? (
                                        /* Vista Tabla Compacta de Alta Densidad */
                                        <div className="vt-table-responsive">
                                            <table className="vt-table">
                                                <thead>
                                                    <tr>
                                                        <th>Usuario / Email</th>
                                                        <th>Plataforma</th>
                                                        <th>ID de Usuario</th>
                                                        <th>Última Sesión</th>
                                                        <th>Estado</th>
                                                        <th>Historial</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredUsers.map((u) => {
                                                        const isRecent = u.lastLogin && (Date.now() - u.lastLogin) < (30 * 24 * 60 * 60 * 1000);
                                                        const isAndroid = (u.platform || '').toLowerCase().includes('android');
                                                        return (
                                                            <tr key={u.userId || Math.random()}>
                                                                <td className="col-email">
                                                                    <div className="user-email-flex">
                                                                        <span className="u-avatar">{isAndroid ? '📱' : '💻'}</span>
                                                                        <span className="u-email-text">{u.userEmail || 'Sin Email'}</span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className={`vt-platform-chip ${isAndroid ? 'android' : 'web'}`}>
                                                                        {isAndroid ? '🤖 Android' : '🌐 Web'}
                                                                    </span>
                                                                </td>
                                                                <td className="col-uid">
                                                                    <code>{u.userId ? `${u.userId.substring(0, 10)}...` : 'N/A'}</code>
                                                                </td>
                                                                <td className="col-date">
                                                                    {u.lastLogin 
                                                                        ? new Date(u.lastLogin).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) 
                                                                        : 'N/A'}
                                                                </td>
                                                                <td>
                                                                    <span className={`vt-status-pill ${isRecent ? 'online' : 'offline'}`}>
                                                                        {isRecent ? '🟢 Reciente' : '⚪ Inactivo'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="vt-history-tags">
                                                                        {u.versionHistory && u.versionHistory.length > 0 ? (
                                                                            u.versionHistory.slice(-2).reverse().map((vh, i) => (
                                                                                <span key={i} className="vt-hist-pill" title={`Última verif: ${vh.lastUpdateCheck ? new Date(vh.lastUpdateCheck).toLocaleDateString() : 'N/A'}`}>
                                                                                    v{vh.versionName}
                                                                                </span>
                                                                            ))
                                                                        ) : (
                                                                            <span className="vt-no-hist">Sin historial</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        /* Vista Tarjetas */
                                        <div className="vt-cards-grid">
                                            {filteredUsers.map((u) => {
                                                const isRecent = u.lastLogin && (Date.now() - u.lastLogin) < (30 * 24 * 60 * 60 * 1000);
                                                const isAndroid = (u.platform || '').toLowerCase().includes('android');
                                                return (
                                                    <div key={u.userId || Math.random()} className="vt-mini-card">
                                                        <div className="vt-mc-header">
                                                            <div className="vt-mc-user">
                                                                <span className="vt-mc-avatar">{isAndroid ? '📱' : '💻'}</span>
                                                                <div className="vt-mc-info">
                                                                    <strong className="vt-mc-email">{u.userEmail || 'Desconocido'}</strong>
                                                                    <span className="vt-mc-uid">ID: {u.userId}</span>
                                                                </div>
                                                            </div>
                                                            <span className={`vt-status-pill ${isRecent ? 'online' : 'offline'}`}>
                                                                {isRecent ? '🟢' : '⚪'}
                                                            </span>
                                                        </div>

                                                        <div className="vt-mc-body">
                                                            <div className="vt-mc-row">
                                                                <span>Plataforma:</span>
                                                                <span className={`vt-platform-chip ${isAndroid ? 'android' : 'web'}`}>
                                                                    {isAndroid ? '🤖 Android' : '🌐 Web'}
                                                                </span>
                                                            </div>
                                                            <div className="vt-mc-row">
                                                                <span>Último acceso:</span>
                                                                <strong>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'N/A'}</strong>
                                                            </div>
                                                            {u.versionHistory && u.versionHistory.length > 0 && (
                                                                <div className="vt-mc-hist">
                                                                    <span>Historial:</span>
                                                                    <div className="vt-history-tags">
                                                                        {u.versionHistory.slice(-2).reverse().map((vh, i) => (
                                                                            <span key={i} className="vt-hist-pill">v{vh.versionName}</span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                ) : (
                                    <div className="vt-empty-users">
                                        🔍 No se encontraron usuarios para los filtros seleccionados en v{selectedVersion}.
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="vt-empty-selection">
                            👈 Selecciona una versión a la izquierda para examinar sus usuarios activos.
                        </div>
                    )}
                </div>

            </div>

            <style>{`
                .vt-container {
                    background: #091322;
                    border: 1px solid rgba(197, 160, 89, 0.2);
                    border-radius: 16px;
                    padding: 20px;
                    color: #e2e8f0;
                    font-family: inherit;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
                }

                .vt-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }

                .vt-header-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .vt-badge-icon {
                    font-size: 1.8rem;
                    background: rgba(197, 160, 89, 0.15);
                    padding: 8px;
                    border-radius: 12px;
                    border: 1px solid rgba(197, 160, 89, 0.3);
                }

                .vt-header-title h2 {
                    margin: 0;
                    font-family: 'Orbitron', sans-serif;
                    font-size: 1.25rem;
                    color: #c5a059;
                    letter-spacing: 1px;
                }

                .vt-header-title p {
                    margin: 2px 0 0;
                    font-size: 0.8rem;
                    color: #94a3b8;
                }

                .vt-btn-refresh {
                    background: linear-gradient(135deg, rgba(197, 160, 89, 0.2) 0%, rgba(158, 125, 59, 0.1) 100%);
                    border: 1px solid #c5a059;
                    color: #fce7f3;
                    color: #c5a059;
                    padding: 8px 16px;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    font-weight: bold;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .vt-btn-refresh:hover:not(:disabled) {
                    background: rgba(197, 160, 89, 0.35);
                    box-shadow: 0 0 12px rgba(197, 160, 89, 0.3);
                }

                .vt-error-banner {
                    background: rgba(239, 68, 68, 0.15);
                    border: 1px solid #ef4444;
                    padding: 10px 14px;
                    border-radius: 8px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: #fca5a5;
                    font-size: 0.85rem;
                }

                .vt-error-banner button {
                    background: none;
                    border: none;
                    color: #ef4444;
                    cursor: pointer;
                    font-size: 1.2rem;
                }

                /* KPI Grid */
                .vt-kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 12px;
                }

                .vt-kpi-card {
                    background: #0e1e33;
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 12px;
                    padding: 14px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .kpi-icon {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }

                .kpi-body {
                    display: flex;
                    flex-direction: column;
                }

                .kpi-label {
                    font-size: 0.65rem;
                    color: #64748b;
                    font-weight: bold;
                    letter-spacing: 0.5px;
                }

                .kpi-value {
                    font-size: 1.4rem;
                    font-weight: 800;
                    color: #f8fafc;
                    line-height: 1.2;
                }

                .kpi-subtext {
                    font-size: 0.7rem;
                    color: #94a3b8;
                }

                /* Segment Bar */
                .vt-segment-container {
                    background: #0e1a2b;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 10px;
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .vt-segment-header {
                    display: flex;
                    justify-content: space-between;
                    font-size: 0.7rem;
                    font-weight: bold;
                    color: #64748b;
                    letter-spacing: 0.5px;
                }

                .vt-timestamp {
                    color: #475569;
                    font-weight: normal;
                }

                .vt-segment-bar {
                    height: 10px;
                    background: #1e293b;
                    border-radius: 5px;
                    overflow: hidden;
                    display: flex;
                    width: 100%;
                }

                .vt-segment-fill {
                    height: 100%;
                    cursor: pointer;
                    transition: opacity 0.2s;
                }

                .vt-segment-fill:hover {
                    opacity: 0.8;
                }

                .vt-segment-legend {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                    font-size: 0.72rem;
                }

                .vt-legend-item {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    cursor: pointer;
                }

                .vt-legend-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .vt-legend-name {
                    color: #cbd5e1;
                    font-weight: bold;
                }

                .vt-legend-pct {
                    color: #64748b;
                }

                /* Main Grid Dual Pane */
                .vt-main-grid {
                    display: grid;
                    grid-template-columns: 280px 1fr;
                    gap: 16px;
                    align-items: start;
                }

                /* Side Panel */
                .vt-side-panel {
                    background: #0a1628;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 14px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .vt-side-header {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .vt-side-header h3 {
                    margin: 0;
                    font-size: 0.9rem;
                    color: #c5a059;
                    font-family: 'Orbitron', sans-serif;
                }

                .vt-mini-search {
                    background: #060e1a;
                    border: 1px solid #1e293b;
                    border-radius: 6px;
                    padding: 6px 10px;
                    color: #fff;
                    font-size: 0.78rem;
                    outline: none;
                }

                .vt-version-scroll {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    max-height: 440px;
                    overflow-y: auto;
                    padding-right: 4px;
                }

                .vt-version-item {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 8px;
                    padding: 8px 10px;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .vt-version-item:hover {
                    background: rgba(255, 255, 255, 0.06);
                }

                .vt-version-item.active {
                    background: rgba(197, 160, 89, 0.15);
                    border-color: #c5a059;
                }

                .vt-ver-item-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .vt-color-indicator {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .vt-ver-title {
                    font-weight: bold;
                    font-size: 0.85rem;
                    color: #f1f5f9;
                }

                .vt-top-chip {
                    background: rgba(52, 211, 153, 0.2);
                    color: #34d399;
                    font-size: 0.6rem;
                    font-weight: bold;
                    padding: 1px 5px;
                    border-radius: 4px;
                }

                .vt-ver-count {
                    font-size: 0.75rem;
                    color: #94a3b8;
                }

                .vt-ver-progress-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .vt-ver-progress-bg {
                    flex: 1;
                    height: 4px;
                    background: #1e293b;
                    border-radius: 2px;
                    overflow: hidden;
                }

                .vt-ver-progress-fill {
                    height: 100%;
                    border-radius: 2px;
                }

                .vt-ver-pct {
                    font-size: 0.7rem;
                    color: #64748b;
                    width: 32px;
                    text-align: right;
                }

                /* Detail Panel */
                .vt-detail-panel {
                    background: #0a1628;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    min-height: 480px;
                }

                .vt-detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }

                .vt-detail-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .vt-version-badge {
                    background: linear-gradient(135deg, #c5a059 0%, #9e7d3b 100%);
                    color: #071322;
                    font-family: 'Orbitron', sans-serif;
                    font-weight: bold;
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-size: 0.9rem;
                }

                .vt-detail-title h4 {
                    margin: 0;
                    font-size: 0.95rem;
                    color: #f8fafc;
                }

                .vt-detail-subtitle {
                    font-size: 0.72rem;
                    color: #64748b;
                }

                .vt-detail-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .vt-view-toggle {
                    display: flex;
                    background: #060e1a;
                    border: 1px solid #1e293b;
                    border-radius: 6px;
                    padding: 2px;
                }

                .vt-view-toggle button {
                    background: transparent;
                    border: none;
                    color: #64748b;
                    padding: 4px 8px;
                    font-size: 0.75rem;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .vt-view-toggle button.active {
                    background: rgba(197, 160, 89, 0.2);
                    color: #c5a059;
                    font-weight: bold;
                }

                .vt-user-search {
                    background: #060e1a;
                    border: 1px solid #1e293b;
                    border-radius: 6px;
                    padding: 6px 12px;
                    color: #fff;
                    font-size: 0.8rem;
                    width: 200px;
                    outline: none;
                }

                /* Table View */
                .vt-user-content-box {
                    max-height: 400px;
                    overflow-y: auto;
                }

                .vt-table-responsive {
                    overflow-x: auto;
                }

                .vt-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8rem;
                    text-align: left;
                }

                .vt-table th {
                    background: #071220;
                    color: #64748b;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 8px 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                    position: sticky;
                    top: 0;
                    z-index: 2;
                }

                .vt-table td {
                    padding: 8px 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
                    color: #cbd5e1;
                }

                .vt-table tr:hover {
                    background: rgba(255, 255, 255, 0.02);
                }

                .user-email-flex {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .u-avatar {
                    font-size: 0.85rem;
                    opacity: 0.7;
                }

                .u-email-text {
                    font-weight: bold;
                    color: #f1f5f9;
                }

                .col-uid code {
                    font-family: monospace;
                    background: rgba(255, 255, 255, 0.05);
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 0.72rem;
                    color: #94a3b8;
                }

                .col-date {
                    color: #94a3b8;
                }

                .vt-status-pill {
                    font-size: 0.7rem;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-weight: bold;
                    display: inline-block;
                }

                .vt-status-pill.online {
                    background: rgba(16, 185, 129, 0.15);
                    color: #34d399;
                    border: 1px solid rgba(16, 185, 129, 0.3);
                }

                .vt-status-pill.offline {
                    background: rgba(148, 163, 184, 0.1);
                    color: #64748b;
                    border: 1px solid rgba(148, 163, 184, 0.2);
                }

                .vt-platform-chip {
                    font-size: 0.7rem;
                    padding: 2px 7px;
                    border-radius: 6px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                    white-space: nowrap;
                }

                .vt-platform-chip.android {
                    background: rgba(34, 197, 94, 0.12);
                    color: #4ade80;
                    border: 1px solid rgba(34, 197, 94, 0.25);
                }

                .vt-platform-chip.web {
                    background: rgba(59, 130, 246, 0.12);
                    color: #60a5fa;
                    border: 1px solid rgba(59, 130, 246, 0.25);
                }

                .vt-history-tags {
                    display: flex;
                    gap: 4px;
                    flex-wrap: wrap;
                }

                .vt-hist-pill {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #94a3b8;
                    font-size: 0.68rem;
                    padding: 1px 6px;
                    border-radius: 4px;
                }

                .vt-no-hist {
                    font-size: 0.7rem;
                    color: #475569;
                    font-style: italic;
                }

                /* Cards Grid */
                .vt-cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 10px;
                }

                .vt-mini-card {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 10px;
                    padding: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .vt-mc-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .vt-mc-user {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    overflow: hidden;
                }

                .vt-mc-avatar {
                    font-size: 1.1rem;
                }

                .vt-mc-info {
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .vt-mc-email {
                    font-size: 0.8rem;
                    color: #f1f5f9;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .vt-mc-uid {
                    font-size: 0.65rem;
                    color: #64748b;
                    font-family: monospace;
                }

                .vt-mc-body {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    font-size: 0.72rem;
                    border-top: 1px solid rgba(255, 255, 255, 0.03);
                    padding-top: 6px;
                }

                .vt-mc-row {
                    display: flex;
                    justify-content: space-between;
                    color: #94a3b8;
                }

                .vt-mc-hist {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: #94a3b8;
                }

                /* States */
                .vt-loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 40px;
                    gap: 12px;
                    color: #94a3b8;
                    font-size: 0.85rem;
                }

                .vt-spinner {
                    width: 24px;
                    height: 24px;
                    border: 3px solid rgba(197, 160, 89, 0.2);
                    border-top-color: #c5a059;
                    border-radius: 50%;
                    animation: vtSpin 0.8s linear infinite;
                }

                @keyframes vtSpin {
                    to { transform: rotate(360deg); }
                }

                .vt-empty-users, .vt-empty-selection {
                    text-align: center;
                    color: #64748b;
                    padding: 60px 20px;
                    font-size: 0.85rem;
                    font-style: italic;
                }

                @media (max-width: 900px) {
                    .vt-main-grid {
                        grid-template-columns: 1fr;
                    }
                    .vt-side-panel {
                        max-height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VersionTrackingPanel;

